import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import {
  ComposedChart, Line, Scatter, XAxis, YAxis, Tooltip, CartesianGrid,
  ResponsiveContainer, Legend, ReferenceLine, ZAxis
} from "recharts";
import { LineChart as LineIcon, Loader2, Save, Bookmark, Trash2, CheckCircle } from "lucide-react";
import debounce from "lodash.debounce";
import { toast } from "sonner";

export default function VariogramFitDialog({ projectId, initialParams, onApply }) {
  const [open, setOpen] = useState(false);
  const [experimental, setExperimental] = useState([]);
  const [sampleVariance, setSampleVariance] = useState(null);
  const [nSamples, setNSamples] = useState(0);
  const [modeled, setModeled] = useState([]);
  const [busy, setBusy] = useState(false);
  const [range, setRange] = useState(initialParams?.variogram_range || 60);
  const [sill, setSill] = useState(initialParams?.variogram_sill || 0.5);
  const [nugget, setNugget] = useState(initialParams?.variogram_nugget || 0.05);
  const [azimuth, setAzimuth] = useState(0);
  const [dip, setDip] = useState(0);
  const [tolerance, setTolerance] = useState(90);
  const [lag, setLag] = useState(20);
  const [nlags, setNlags] = useState(12);
  const [presets, setPresets] = useState([]);
  const [newPresetName, setNewPresetName] = useState("");
  const [cvResult, setCvResult] = useState(null);
  const [cvBusy, setCvBusy] = useState(false);

  const loadPresets = () =>
    api.get(`/projects/${projectId}/variogram-presets`).then((r) => setPresets(r.data));

  const loadExperimental = useMemo(() => debounce((lg, nl, az, dp, tol) => {
    setBusy(true);
    api.get(`/projects/${projectId}/analytics/variogram/experimental`, {
      params: { lag: lg, nlags: nl, azimuth: az, dip: dp, tolerance_deg: tol },
    }).then((r) => {
      setExperimental((r.data.lags || []).filter((x) => x.gamma !== null));
      setSampleVariance(r.data.sample_variance);
      setNSamples(r.data.n_samples || 0);
    }).finally(() => setBusy(false));
  }, 200), [projectId]);

  const loadModeled = useMemo(() => debounce((r, s, n) => {
    api.post(`/projects/${projectId}/analytics/variogram/model`, {
      range: r, sill: s, nugget: n,
    }).then((res) => setModeled(res.data.points));
  }, 150), [projectId]);

  useEffect(() => { if (open) { loadPresets(); loadExperimental(lag, nlags, azimuth, dip, tolerance); } }, [open, lag, nlags, azimuth, dip, tolerance]);  // eslint-disable-line
  useEffect(() => { if (open) loadModeled(range, sill, nugget); }, [open, range, sill, nugget]);  // eslint-disable-line

  const savePreset = async () => {
    const name = newPresetName.trim() || `Preset ${new Date().toISOString().slice(0, 16)}`;
    try {
      await api.post(`/projects/${projectId}/variogram-presets`, {
        name, range, sill, nugget, azimuth, dip, anisotropy: 1.0,
      });
      toast.success(`Saved preset "${name}"`);
      setNewPresetName("");
      loadPresets();
    } catch { toast.error("Failed to save preset"); }
  };

  const loadPreset = (p) => {
    setRange(p.range); setSill(p.sill); setNugget(p.nugget);
    setAzimuth(p.azimuth || 0); setDip(p.dip || 0);
  };

  const deletePreset = async (id) => {
    await api.delete(`/projects/${projectId}/variogram-presets/${id}`);
    loadPresets();
  };

  const runCV = async () => {
    setCvBusy(true);
    try {
      const { data } = await api.post(`/projects/${projectId}/analytics/kriging/cv`, { range, sill, nugget });
      setCvResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "CV failed");
    } finally { setCvBusy(false); }
  };

  const apply = () => {
    onApply?.({ variogram_range: range, variogram_sill: sill, variogram_nugget: nugget,
      variogram_azimuth: azimuth, variogram_dip: dip });
    setOpen(false);
  };

  /**
   * Session I-8 · Auto-fit (approved same session)
   *
   * Coarse-to-fine pattern search over (range, sill, nugget) minimising
   * pair-count-weighted sum-of-squared residuals against the currently
   * loaded experimental variogram. Runs entirely client-side — no
   * network round-trip — so the button feels instant even on real
   * 400-assay datasets.
   *
   * Search bounds are anchored to the observed data:
   *   range    ∈ [max_h * 0.15,  max_h * 1.8]
   *   sill     ∈ [σ² * 0.6,       σ² * 2.0]
   *   nugget   ∈ [0,               σ² * 0.6]
   * This keeps the fit sensible for any commodity unit scale — the
   * same brittle unit-scale assumption that produced Session I-7's
   * sill-slider bug is avoided by deriving bounds from the data.
   *
   * Spherical model: γ(h) = nugget + (sill-nugget)·(1.5·u - 0.5·u³)
   * for u = h/range ≤ 1; γ(h) = sill for h ≥ range.
   */
  const [fitBusy, setFitBusy] = useState(false);
  const autoFit = () => {
    if (!experimental.length || sampleVariance == null) {
      toast.error("Load experimental variogram first (adjust lag / nlags).");
      return;
    }
    setFitBusy(true);
    // Yield to the UI so the busy spinner renders before we start work.
    setTimeout(() => {
      try {
        // Extract h, γ, pair-weight arrays.
        const pts = experimental
          .filter(e => Number.isFinite(e.h) && Number.isFinite(e.gamma))
          .map(e => ({ h: e.h, g: e.gamma, w: Math.max(1, e.pairs || 1) }));
        if (pts.length < 3) {
          toast.error("Need at least 3 experimental lags to auto-fit.");
          setFitBusy(false);
          return;
        }
        const maxH = Math.max(...pts.map(p => p.h));
        const sv   = sampleVariance;

        const spherical = (h, rng, s, ng) => {
          if (h >= rng) return s;
          const u = h / rng;
          return ng + (s - ng) * (1.5 * u - 0.5 * u * u * u);
        };
        const wss = (rng, s, ng) => {
          let acc = 0;
          for (const p of pts) {
            const m = spherical(p.h, rng, s, ng);
            const d = m - p.g;
            acc += p.w * d * d;
          }
          return acc;
        };

        // Coarse pattern search (12×10×8 = 960 evaluations — cheap).
        const rangeGrid  = 12, sillGrid = 10, nugGrid = 8;
        const rMin = Math.max(1, maxH * 0.15), rMax = maxH * 1.8;
        const sMin = sv * 0.6,                 sMax = sv * 2.0;
        const nMin = 0,                        nMax = sv * 0.6;
        let best = { r: range, s: sill, n: nugget, err: wss(range, sill, nugget) };
        for (let i = 0; i < rangeGrid; i++) {
          const r = rMin + (rMax - rMin) * i / (rangeGrid - 1);
          for (let j = 0; j < sillGrid; j++) {
            const s = sMin + (sMax - sMin) * j / (sillGrid - 1);
            for (let k = 0; k < nugGrid; k++) {
              const n = nMin + (nMax - nMin) * k / (nugGrid - 1);
              if (n >= s) continue;  // nugget must stay below sill
              const err = wss(r, s, n);
              if (err < best.err) best = { r, s, n, err };
            }
          }
        }
        // Refine around the coarse winner (5×5×5 = 125 evaluations).
        const refineWindow = { r: (rMax - rMin) / rangeGrid,
                               s: (sMax - sMin) / sillGrid,
                               n: (nMax - nMin) / nugGrid };
        for (let pass = 0; pass < 3; pass++) {
          const rSpan = refineWindow.r, sSpan = refineWindow.s, nSpan = refineWindow.n;
          for (let i = -2; i <= 2; i++) {
            const r = Math.max(1, best.r + i * rSpan / 4);
            for (let j = -2; j <= 2; j++) {
              const s = Math.max(0.0001, best.s + j * sSpan / 4);
              for (let k = -2; k <= 2; k++) {
                const n = Math.max(0, best.n + k * nSpan / 4);
                if (n >= s) continue;
                const err = wss(r, s, n);
                if (err < best.err) best = { r, s, n, err };
              }
            }
          }
          refineWindow.r /= 4; refineWindow.s /= 4; refineWindow.n /= 4;
        }
        // Apply — seededSillRef locked so the auto-seed effect doesn't
        // clobber the fit on next re-render.
        seededSillRef.current = true;
        setRange(Number(best.r.toFixed(1)));
        setSill(Number(best.s.toFixed(best.s > 20 ? 1 : 3)));
        setNugget(Number(best.n.toFixed(best.n > 20 ? 1 : 3)));
        const rmse = Math.sqrt(best.err / pts.map(p => p.w).reduce((a, b) => a + b, 0));
        toast.success(
          `Auto-fit · range=${best.r.toFixed(0)} · sill=${best.s.toFixed(best.s > 20 ? 0 : 2)} · nugget=${best.n.toFixed(best.n > 20 ? 0 : 2)} · rmse=${rmse.toFixed(2)}`
        );
      } catch (err) {
        toast.error("Auto-fit failed: " + (err?.message || String(err)));
      } finally {
        setFitBusy(false);
      }
    }, 0);
  };

  // Session I-7 fix — Sill / Nugget slider ranges must scale with the
  // observed sample variance. Previously the sliders were hard-clamped
  // to [0.01, 2.0] and [0, 1.0] respectively, which made sense only for
  // Cu %-scale deposits where variance ≈ 0.5. On a Fe% deposit (sample
  // variance ~577), the sill could never exceed 2.0 → the modelled
  // spherical curve capped at 2.0 while the experimental points
  // plateaued at 577, so the blue "Model" line looked flat/near-zero
  // across the whole chart. We now derive slider bounds from
  // `sampleVariance` when it's available (raw semivariance mode) and
  // auto-nudge the default sill to `sampleVariance` on first load so
  // the model curve starts on the right order of magnitude for any
  // commodity.
  const sillMax   = sampleVariance != null ? Math.max(2.0, sampleVariance * 1.5) : 2.0;
  const sillStep  = sillMax > 20 ? 1 : sillMax > 2 ? 0.1 : 0.01;
  const nuggetMax = sampleVariance != null ? Math.max(1.0, sampleVariance * 0.5) : 1.0;
  const nuggetStep = nuggetMax > 20 ? 1 : nuggetMax > 2 ? 0.1 : 0.01;

  // Auto-suggest sill = sample_variance when the sample variance first
  // arrives AND the current sill is a small placeholder (< 10 % of the
  // observed variance) — a strong signal the user hasn't picked a
  // deposit-specific value yet. Never overrides a value the user has
  // clearly dragged (heuristic threshold).
  const seededSillRef = useRef(false);
  useEffect(() => {
    if (sampleVariance == null || seededSillRef.current) return;
    if (sill < sampleVariance * 0.1) {
      setSill(Number(sampleVariance.toFixed(sampleVariance > 20 ? 0 : 2)));
      seededSillRef.current = true;
    }
  }, [sampleVariance]);  // eslint-disable-line react-hooks/exhaustive-deps

  const chartData = modeled.map((m) => {
    const exp = experimental.find((e) => Math.abs(e.h - m.h) < lag);
    return { h: m.h, model: m.gamma, exp: exp ? exp.gamma : null };
  });
  const expOnly = experimental.map((e) => ({ h: e.h, exp: e.gamma, model: null, pairs: e.pairs }));

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline"
          data-testid="variogram-fit-button"
          className="rounded-none h-8 font-mono text-[10px] uppercase tracking-widest border-border">
          <LineIcon className="h-3.5 w-3.5 mr-1" /> Fit variogram
        </Button>
      </DialogTrigger>
      <DialogContent className="rounded-none border-border bg-card sm:max-w-4xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-heading tracking-tight">Interactive variogram fit</DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Adjust range, sill, nugget, and direction to fit the modeled variogram to your data. Save reusable presets. Cross-validate with leave-one-out.
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-4">
          <div className="border border-border bg-background p-2" style={{ height: 320 }}>
            <ResponsiveContainer>
              <ComposedChart data={[...chartData, ...expOnly]} margin={{ top: 8, right: 12, bottom: 4, left: 0 }}>
                <CartesianGrid stroke="#1F2430" />
                <XAxis dataKey="h" type="number"
                  tick={{ fontSize: 10, fill: "#94A3B8", fontFamily: "JetBrains Mono" }}
                  label={{ value: "h (m)", fill: "#94A3B8", fontSize: 10, dy: 12 }} />
                <YAxis tick={{ fontSize: 10, fill: "#94A3B8", fontFamily: "JetBrains Mono" }}
                  label={{ value: "γ(h)", fill: "#94A3B8", fontSize: 10, angle: -90, dx: -6 }} />
                <Tooltip
                  contentStyle={{ background: "#0B0D12", border: "1px solid #2A2F3A", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 11 }}
                  labelStyle={{ color: "#F1F5F9" }} />
                <Legend wrapperStyle={{ fontSize: 10, fontFamily: "JetBrains Mono" }} />
                {sampleVariance != null && (
                  <ReferenceLine y={sampleVariance} stroke="#64748B" strokeDasharray="4 4"
                    label={{ value: "sample var", fill: "#94A3B8", fontSize: 9 }} />
                )}
                <ReferenceLine y={sill} stroke="#FF5722" strokeDasharray="2 4" opacity={0.6} />
                <Line type="monotone" dataKey="model" stroke="#00E5FF" strokeWidth={2} dot={false}
                  name="Model (spherical)" isAnimationActive={false} />
                <Scatter dataKey="exp" fill="#FBBF24" name="Experimental" />
              </ComposedChart>
            </ResponsiveContainer>
          </div>

          <div className="space-y-3">
            {/* Session I-8 — Auto-fit button sits above the sliders so
                the user can go from "computed experimental" to "fitted
                model" in one click. Least-squares over range/sill/
                nugget runs fully in the browser (no round-trip). */}
            <Button
              onClick={autoFit}
              disabled={busy || fitBusy || !experimental.length}
              size="sm"
              variant="outline"
              data-testid="variogram-autofit-button"
              className="w-full font-mono text-[11px] uppercase tracking-widest border-primary text-primary hover:bg-primary hover:text-primary-foreground">
              {fitBusy
                ? (<><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin"/>Fitting…</>)
                : (<><CheckCircle className="h-3.5 w-3.5 mr-1.5"/>Auto-fit to experimental</>)}
            </Button>
            <div>
              <div className="smin-label mb-1">Range · {range.toFixed(0)} m</div>
              <Slider min={5} max={300} step={1} value={[range]}
                onValueChange={(v) => setRange(v[0])}
                data-testid="variogram-range-slider" />
            </div>
            <div>
              <div className="smin-label mb-1">
                Sill · {sill.toFixed(sillMax > 20 ? 0 : 2)}
                {sampleVariance != null && (
                  <span className="ml-2 text-[9px] opacity-60">
                    (σ² = {Number(sampleVariance).toFixed(sampleVariance > 20 ? 0 : 2)})
                  </span>
                )}
              </div>
              <Slider min={0.01} max={sillMax} step={sillStep} value={[sill]}
                onValueChange={(v) => { seededSillRef.current = true; setSill(v[0]); }}
                data-testid="variogram-sill-slider" />
            </div>
            <div>
              <div className="smin-label mb-1">Nugget · {nugget.toFixed(nuggetMax > 20 ? 0 : 2)}</div>
              <Slider min={0} max={nuggetMax} step={nuggetStep} value={[nugget]}
                onValueChange={(v) => setNugget(v[0])}
                data-testid="variogram-nugget-slider" />
            </div>
            <div className="border-t border-border pt-3">
              <div className="smin-label mb-1">Direction</div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <div className="text-[10px] text-muted-foreground font-mono">Az {azimuth.toFixed(0)}°</div>
                  <Slider min={0} max={360} step={5} value={[azimuth]}
                    onValueChange={(v) => setAzimuth(v[0])}
                    data-testid="variogram-azimuth-slider" />
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground font-mono">Dip {dip.toFixed(0)}°</div>
                  <Slider min={-90} max={0} step={5} value={[dip]}
                    onValueChange={(v) => setDip(v[0])}
                    data-testid="variogram-dip-slider" />
                </div>
                <div>
                  <div className="text-[10px] text-muted-foreground font-mono">Tol {tolerance.toFixed(0)}°</div>
                  <Slider min={10} max={90} step={5} value={[tolerance]}
                    onValueChange={(v) => setTolerance(v[0])}
                    data-testid="variogram-tolerance-slider" />
                </div>
              </div>
              <div className="text-[10px] text-muted-foreground font-mono mt-1">
                {tolerance >= 90 ? "Omnidirectional" : `Directional filter active`}
              </div>
            </div>
            <div className="border-t border-border pt-3 font-mono text-[11px] text-muted-foreground">
              {busy ? (
                <div className="flex items-center gap-2"><Loader2 className="h-3 w-3 animate-spin" /> Fitting…</div>
              ) : (
                <>n = {nSamples} · σ² = {sampleVariance ?? "—"}</>
              )}
            </div>
          </div>
        </div>

        {/* Presets + CV row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-4">
          <div className="border border-border p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="smin-label flex items-center gap-1"><Bookmark className="h-3 w-3" /> Presets</div>
            </div>
            <div className="flex items-center gap-2 mb-2">
              <Input value={newPresetName} onChange={(e) => setNewPresetName(e.target.value)}
                data-testid="variogram-preset-name-input"
                placeholder="Preset name" className="rounded-none bg-input font-mono text-xs h-8" />
              <Button size="sm" onClick={savePreset}
                data-testid="variogram-preset-save-button"
                className="rounded-none h-8 font-mono text-[10px] uppercase tracking-widest">
                <Save className="h-3 w-3 mr-1" /> Save
              </Button>
            </div>
            <div className="space-y-1 max-h-32 overflow-y-auto">
              {presets.length === 0 && <div className="text-xs text-muted-foreground">No presets yet.</div>}
              {presets.map((p) => (
                <div key={p.id} data-testid="variogram-preset-row"
                  className="flex items-center gap-2 border border-border p-1.5 font-mono text-xs">
                  <button onClick={() => loadPreset(p)}
                    data-testid="variogram-preset-load-button"
                    className="min-w-0 flex-1 text-left truncate hover:text-primary">
                    {p.name}
                  </button>
                  <span className="text-muted-foreground text-[10px]">R{p.range} S{p.sill} N{p.nugget}</span>
                  <button onClick={() => deletePreset(p.id)}
                    className="p-1 hover:text-destructive"><Trash2 className="h-3 w-3" /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="border border-border p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="smin-label">Cross-validation (leave-one-out)</div>
              <Button size="sm" onClick={runCV} disabled={cvBusy}
                data-testid="variogram-cv-button"
                className="rounded-none h-7 font-mono text-[10px] uppercase tracking-widest">
                {cvBusy ? <Loader2 className="h-3 w-3 animate-spin" /> : <CheckCircle className="h-3 w-3 mr-1" />}
                Run CV
              </Button>
            </div>
            {cvResult ? (
              <>
                <div className="grid grid-cols-3 gap-2 font-mono text-xs">
                  <div><div className="smin-label">n</div><div>{cvResult.n_samples}</div></div>
                  <div><div className="smin-label">MSE</div><div className="text-primary">{cvResult.mse}</div></div>
                  <div><div className="smin-label">MAE</div><div>{cvResult.mae}</div></div>
                  <div className="col-span-3"><div className="smin-label">R²</div><div className={cvResult.r2 >= 0 ? "text-secondary" : "text-destructive"}>{cvResult.r2}</div></div>
                </div>
                <div style={{ height: 100 }} className="mt-2">
                  <ResponsiveContainer>
                    <ComposedChart data={cvResult.scatter} margin={{ top: 4, right: 4, bottom: 0, left: -18 }}>
                      <CartesianGrid stroke="#1F2430" />
                      <XAxis dataKey="actual" type="number" tick={{ fontSize: 8, fill: "#94A3B8" }} />
                      <YAxis dataKey="predicted" type="number" tick={{ fontSize: 8, fill: "#94A3B8" }} />
                      <ZAxis range={[20, 20]} />
                      <Tooltip
                        contentStyle={{ background: "#0B0D12", border: "1px solid #2A2F3A", borderRadius: 0, fontFamily: "JetBrains Mono", fontSize: 10 }} />
                      <Scatter dataKey="predicted" fill="#00E5FF" />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </>
            ) : (
              <div className="text-xs text-muted-foreground">Click Run CV to compute leave-one-out MSE/MAE/R² for these parameters.</div>
            )}
          </div>
        </div>

        <div className="mt-3 flex justify-end">
          <Button onClick={apply} disabled={busy}
            data-testid="variogram-apply-button"
            className="rounded-none font-heading">
            Apply to kriging
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
